import React, { useState, useEffect } from 'react';
import { Route, Switch, Redirect } from "react-router-dom";
import { Routes } from "../routes";

// pages
import Groups from "./Groups";
import GroupTransactions from "./GroupTransactions";
import GroupRecordIncome from "./GroupRecordIncome";
import GroupRecordExpenses from "./GroupRecordExpenses";
import GroupMembers from "./GroupMembers";
import ExpenseCategories from "./ExpenseCategories";
import ContributionPurposes from "./ContributionPurposes";
import GroupContributionPurposes from "./GroupContributionPurposes";
import Settings from "./Settings";
import Users from "./Users";
import PublicHome from "./PublicHome";
import Signin from "./examples/Signin";
import Signup from "./examples/Signup";
import ForgotPassword from "./examples/ForgotPassword";
import NotFoundPage from "./examples/NotFound";
import Sidebar from "../components/Sidebar";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Preloader from "../components/Preloader";
import OfflineNotice from "../components/OfflineNotice";
import { flushSyncQueue } from "../utils/syncQueue";
import { apiRequest, getAuthToken } from "../utils/api";

const RouteWithLoader = ({ component: Component, ...rest }) => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Route {...rest} render={props => ( <> <Preloader show={loaded ? false : true} /> <Component {...props} /> </> ) } />
  );
};

const RouteWithSidebar = ({ component: Component, ...rest }) => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Route {...rest} render={props => (
      <>
        <Preloader show={loaded ? false : true} />
        <Sidebar />

        <main className="content">
          <Navbar />
          <OfflineNotice />
          <Component {...props} />
          <Footer />
        </main>
      </>
    )}
    />
  );
};

const SyncListener = () => {
  useEffect(() => {
    const handleOnline = () => {
      flushSyncQueue();
    };
    flushSyncQueue();
    window.addEventListener("online", handleOnline);
    return () => window.removeEventListener("online", handleOnline);
  }, []);
  return null;
};

/** Refresh role/name from API so admins see correct access without re-login. */
const SessionRefresh = () => {
  useEffect(() => {
    if (!getAuthToken()) return;
    let cancelled = false;
    apiRequest("/auth/me")
      .then((data) => {
        if (cancelled || !data?.user) return;
        localStorage.setItem("treasurer.authUser", JSON.stringify(data.user));
        window.dispatchEvent(new Event("treasurer-auth-refresh"));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);
  return null;
};

const isAuthenticated = () => Boolean(localStorage.getItem("treasurer.authToken"));

const RedirectToGroups = () => <Redirect to={Routes.Groups.path} />;
const RedirectToHome = () => <Redirect to={Routes.Presentation.path} />;

const PublicOnlyRoute = ({ component: Component, ...rest }) => {
  return (
    <Route
      {...rest}
      render={(props) =>
        isAuthenticated() ? <RedirectToGroups /> : <Component {...props} />
      }
    />
  );
};

const PrivateRouteWithSidebar = ({ component: Component, ...rest }) => {
  return (
    <Route
      {...rest}
      render={(props) =>
        isAuthenticated() ? (
          <RouteWithSidebar {...props} component={Component} />
        ) : (
          <RedirectToHome />
        )
      }
    />
  );
};

export default () => (
  <>
    <SyncListener />
    <SessionRefresh />
    <Switch>
      <RouteWithLoader exact path={Routes.Presentation.path} component={PublicHome} />
      <PublicOnlyRoute exact path={Routes.Signin.path} component={Signin} />
      <PublicOnlyRoute exact path={Routes.Signup.path} component={Signup} />
      <PublicOnlyRoute exact path={Routes.ForgotPassword.path} component={ForgotPassword} />
      <RouteWithLoader exact path={Routes.NotFound.path} component={NotFoundPage} />

      {/* pages */}
      <PrivateRouteWithSidebar exact path={Routes.Groups.path} component={Groups} />
      <PrivateRouteWithSidebar exact path={Routes.GroupTransactions.path} component={GroupTransactions} />
      <PrivateRouteWithSidebar exact path={Routes.GroupRecordIncome.path} component={GroupRecordIncome} />
      <PrivateRouteWithSidebar exact path={Routes.GroupRecordExpenses.path} component={GroupRecordExpenses} />
      <PrivateRouteWithSidebar exact path={Routes.GroupMembers.path} component={GroupMembers} />
      <PrivateRouteWithSidebar exact path={Routes.GroupContributionPurposes.path} component={GroupContributionPurposes} />
      <PrivateRouteWithSidebar exact path={Routes.ExpenseCategories.path} component={ExpenseCategories} />
      <PrivateRouteWithSidebar exact path={Routes.ContributionPurposes.path} component={ContributionPurposes} />
      <PrivateRouteWithSidebar exact path={Routes.Users.path} component={Users} />
      <PrivateRouteWithSidebar exact path={Routes.Settings.path} component={Settings} />

      <Redirect to={Routes.NotFound.path} />
    </Switch>
  </>
);
